import React, { useEffect, useMemo, useState, useRef  } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  TextField,
  InputAdornment,
  ButtonBase,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import PositionsTable from "../../components/Portfolio/PositionsTable";
import LedgerTable from "../../components/Portfolio/LedgerTable";

import { usePortfolio } from "../../hooks/usePortfolio";
import TransferCryptoModal from "../../components/Portfolio/TransferCryptoModal";
import DepositWithdrawModal from "../../components/Portfolio/DepositWithdrawModal";
import WalletDepositSwapModal from "../../components/Portfolio/WalletDepositSwapModal";
import { scanMultiChainAvail } from "../../utils/scanMultiChainAvail";
import { useWalletAuth } from "../../../components/WalletConnect";
import { useAccountUsdBalances } from "../../hooks/useAccountUsdBalances";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.94) 0%, rgba(10,13,17,.94) 100%)",
  border: "1px solid rgba(255,255,255,.10)",
  text: "#e5e7eb",
  sub: "#9ca3af",
};

// ВАЖНО: фиксируем высоту верхних карточек — тогда ничего не “едет”
const TOP_CARD_H = 230;

const fmtUsd = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "$0.00";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const fmtPct = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00%";
  const sign = n < 0 ? "-" : "";
  return `${sign}${Math.abs(n).toFixed(2)}%`;
};

const pillSx = (active) => ({
  px: 1.1,
  py: 0.55,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.14)",
  bgcolor: active ? "rgba(56,189,248,.14)" : "rgba(15,23,42,.45)",
  color: active ? "#e5e7eb" : "rgba(226,232,240,.75)",
  fontSize: 12,
  fontWeight: 950,
  "&:hover": {
    bgcolor: active ? "rgba(56,189,248,.18)" : "rgba(148,163,184,.08)",
  },
});

function GlassCard({ title, right, children, sx }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        background: GLASS.bg,
        border: GLASS.border,
        boxShadow: "0 18px 40px rgba(15,23,42,.9)",
        overflow: "hidden",

        // КРИТИЧНО: чтобы карточка ВСЕГДА занимала всю ячейку Grid
        width: "100%",
        height: "100%",

        ...sx,
      }}
    >
      <CardContent
        sx={{
          p: 2.1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {(title || right) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.2,
              gap: 1.2,
            }}
          >
            <Typography sx={{ fontWeight: 950, fontSize: 14, color: GLASS.text }}>
              {title}
            </Typography>
            {right}
          </Box>
        )}

        {/* чтобы контент не ломал высоту */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

// fallback: берём pnl как есть
function pickPnLByRange(pnl, rangeKey) {
  if (!pnl) return 0;

  const map = {
    "1D": pnl.day ?? pnl.today ?? pnl["1d"] ?? pnl.todayPnl,
    "7D": pnl.week ?? pnl["7d"] ?? pnl.weekPnl,
    "1M": pnl.month ?? pnl["1m"] ?? pnl.monthPnl,
    "ALL":
      pnl.all ??
      pnl.total ??
      pnl.allTime ??
      (Number(pnl.realized || 0) + Number(pnl.unrealized || 0)),
  };

  const v = map[rangeKey];
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Rewards без вложенной Card (иначе размеры/тени и “едет” layout)
function RewardsInline({ points = 0 }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography sx={{ fontWeight: 950, fontSize: 40, letterSpacing: -1, color: "#38bdf8" }}>
        {Number(points || 0).toFixed(0)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: GLASS.sub, fontWeight: 900 }}>
        Points
      </Typography>
      <Typography sx={{ mt: 0.4, color: GLASS.sub, fontSize: 12, lineHeight: 1.55 }}>
        Rewards counter (referrals / promo rewards) подключим сюда.
      </Typography>
    </Box>
  );
}

export default function PortfolioPanel() {
  const { balances, pnl, positions, ledger, loading } = usePortfolio();
  const acc = useAccountUsdBalances(); // Firestore realtime TOTAL/AVAILABLE

  const { address, isConnected, chainIdNum, connectWalletOnly } = useWalletAuth();

  const [walletOpen, setWalletOpen] = useState(false);
  const [walletMode, setWalletMode] = useState("deposit");
  const [walletDepositOpen, setWalletDepositOpen] = useState(false);
  const [transferCryptoOpen, setTransferCryptoOpen] = useState(false);

  const [multiAvail, setMultiAvail] = useState([]);
  const [multiLoading, setMultiLoading] = useState(false);
  const [multiErr, setMultiErr] = useState("");

  const [rightTab, setRightTab] = useState("pnl"); // pnl | rewards
  const [pnlRange, setPnlRange] = useState("1M"); // 1D | 7D | 1M | ALL

  const [mainTab, setMainTab] = useState("positions"); // positions | orders | history
  const [search, setSearch] = useState("");
  const lastScanKeyRef = useRef("");


useEffect(() => {
  if (!isConnected || !address) {
    lastScanKeyRef.current = "";
    return;
  }

  const scanKey = `${String(address).toLowerCase()}:${Number(chainIdNum || 56)}`;
  if (lastScanKeyRef.current === scanKey) return;
  lastScanKeyRef.current = scanKey;

  let alive = true;
  (async () => {
    try {
      setMultiErr("");
      setMultiLoading(true);
      const data = await scanMultiChainAvail({
        address,
        walletChainId: Number(chainIdNum || 56),
      });
      if (!alive) return;
      setMultiAvail(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!alive) return;
      setMultiErr(e?.message || "Scan failed");
    } finally {
      if (alive) setMultiLoading(false);
    }
  })();

  return () => { alive = false; };
}, [isConnected, address, chainIdNum]);





  const totalUsd = useMemo(() => (acc?.loading ? 0 : Number(acc?.totalUsd || 0)), [acc]);
  const availableUsd = useMemo(() => (acc?.loading ? 0 : Number(acc?.availableUsd || 0)), [acc]);

  // Today delta (приоритет: balances.todayDeltaUsd/todayDeltaPct; иначе fallback pnl 1D)
  const todayDeltaUsd = useMemo(() => {
    const direct =
      balances?.todayDeltaUsd ??
      balances?.balanceChangeTodayUsd ??
      pnl?.todayDelta ??
      pnl?.balanceChangeTodayUsd;

    if (direct != null && Number.isFinite(Number(direct))) return Number(direct);
    return pickPnLByRange(pnl, "1D");
  }, [balances, pnl]);

  const todayDeltaPct = useMemo(() => {
    const direct =
      balances?.todayDeltaPct ??
      balances?.balanceChangeTodayPct ??
      pnl?.todayDeltaPct ??
      pnl?.balanceChangeTodayPct;

    if (direct != null && Number.isFinite(Number(direct))) return Number(direct);

    const prev = Number(totalUsd) - Number(todayDeltaUsd);
    if (!Number.isFinite(prev) || prev === 0) return 0;
    return (Number(todayDeltaUsd) / prev) * 100;
  }, [balances, pnl, totalUsd, todayDeltaUsd]);

  const todayTone = todayDeltaUsd >= 0 ? "#22c55e" : "#f97373";

  const pnlValue = useMemo(() => pickPnLByRange(pnl, pnlRange), [pnl, pnlRange]);
  const pnlTone = pnlValue >= 0 ? "#22c55e" : "#f97373";

  const filteredPositions = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return positions || [];
    return (positions || []).filter((p) => {
      const s = `${p.marketTitle || ""} ${p.marketId || ""} ${p.side || ""}`.toLowerCase();
      return s.includes(q);
    });
  }, [positions, search]);

  const filteredLedger = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return ledger || [];
    return (ledger || []).filter((e) => {
      const s = `${e.type || ""} ${e.marketTitle || ""} ${e.marketId || ""} ${e.txHash || ""}`.toLowerCase();
      return s.includes(q);
    });
  }, [ledger, search]);

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "transparent", color: "#e5e7eb" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* РОВНО 3 БЛОКА */}
        <Grid container spacing={2} alignItems="stretch">
          {/* 1) TOP LEFT */}
          <Grid item xs={12} md={6}>
            <GlassCard
              title="Portfolio"
              sx={{ height: TOP_CARD_H }} // <= одинаковая высота
              right={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setWalletMode("deposit");
                      setWalletOpen(true);
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 1000,
                      borderRadius: 2,
                      px: 2.2,
                      background:
                        "linear-gradient(135deg, rgba(56,189,248,1) 0%, rgba(59,130,246,1) 55%, rgba(99,102,241,1) 120%)",
                      boxShadow: "0 12px 30px rgba(56,189,248,.18)",
                      "&:hover": { filter: "brightness(1.05)" },
                    }}
                  >
                    Deposit
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setWalletMode("withdraw");
                      setWalletOpen(true);
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 950,
                      borderRadius: 2,
                      px: 2.2,
                      borderColor: "rgba(148,163,184,.55)",
                      color: "#cbd5e1",
                      "&:hover": {
                        borderColor: "rgba(56,189,248,.55)",
                        bgcolor: "rgba(56,189,248,.08)",
                      },
                    }}
                  >
                    Withdraw
                  </Button>
                </Box>
              }
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "baseline", flexWrap: "wrap" }}>
                <Box sx={{ minWidth: 220 }}>
                  <Typography sx={{ fontSize: 12, color: GLASS.sub, fontWeight: 900 }}>
                    TOTAL
                  </Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 40, fontWeight: 1150, letterSpacing: -1 }}>
                    {acc?.loading ? "Loading…" : fmtUsd(totalUsd)}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: 180 }}>
                  <Typography sx={{ fontSize: 12, color: GLASS.sub, fontWeight: 900 }}>
                    AVAILABLE
                  </Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 28, fontWeight: 1050, letterSpacing: -0.5 }}>
                    {acc?.loading ? "Loading…" : fmtUsd(availableUsd)}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ mt: 0.8, fontSize: 13, fontWeight: 950, color: todayTone }}>
                {fmtUsd(todayDeltaUsd)} ({fmtPct(todayDeltaPct)}){" "}
                <span style={{ color: "rgba(226,232,240,.65)", fontWeight: 900 }}>Today</span>
              </Typography>

              <Box sx={{ flex: 1 }} />
              <Divider sx={{ mt: 1.4, borderColor: "rgba(148,163,184,.16)" }} />
            </GlassCard>
          </Grid>

          {/* 2) TOP RIGHT */}
          <Grid item xs={12} md={6}>
            <GlassCard
              title={rightTab === "pnl" ? "Profit / Loss" : "Rewards"}
              sx={{ height: TOP_CARD_H }} // <= одинаковая высота
              right={
                <Box sx={{ display: "flex", gap: 0.8 }}>
                  <ButtonBase onClick={() => setRightTab("pnl")} sx={pillSx(rightTab === "pnl")}>
                    P/L
                  </ButtonBase>
                  <ButtonBase onClick={() => setRightTab("rewards")} sx={pillSx(rightTab === "rewards")}>
                    Rewards
                  </ButtonBase>
                </Box>
              }
            >
              {rightTab === "pnl" ? (
                <>
                  <Box sx={{ display: "flex", gap: 0.8, mb: 1.2, flexWrap: "wrap" }}>
                    {["1D", "7D", "1M", "ALL"].map((k) => (
                      <ButtonBase key={k} onClick={() => setPnlRange(k)} sx={pillSx(pnlRange === k)}>
                        {k}
                      </ButtonBase>
                    ))}
                  </Box>

                  <Typography sx={{ fontSize: 40, fontWeight: 1150, letterSpacing: -1, color: pnlTone }}>
                    {loading ? "…" : fmtUsd(pnlValue)}
                  </Typography>

                  <Typography sx={{ mt: 0.6, fontSize: 12, color: GLASS.sub }}>
                    {pnlRange === "1D"
                      ? "Past day"
                      : pnlRange === "7D"
                      ? "Past 7 days"
                      : pnlRange === "1M"
                      ? "Past month"
                      : "All time"}
                  </Typography>

                  <Box sx={{ flex: 1 }} />
                  <Divider sx={{ mt: 1.4, borderColor: "rgba(148,163,184,.16)" }} />
                </>
              ) : (
                <RewardsInline points={balances?.points ?? 0} />
              )}
            </GlassCard>
          </Grid>

          {/* 3) BOTTOM BIG */}
          <Grid item xs={12}>
            <GlassCard
              title="Activity"
              right={
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search markets / tx / type…"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "rgba(226,232,240,.55)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", sm: 360 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "rgba(15,23,42,.45)",
                      color: "#e5e7eb",
                      "& fieldset": { borderColor: "rgba(148,163,184,.25)" },
                      "&:hover fieldset": { borderColor: "rgba(56,189,248,.35)" },
                      "&.Mui-focused fieldset": { borderColor: "rgba(56,189,248,.55)" },
                    },
                    "& input::placeholder": { color: "rgba(226,232,240,.45)" },
                  }}
                />
              }
            >
              <Tabs
                value={mainTab}
                onChange={(_, v) => setMainTab(v)}
                textColor="inherit"
                indicatorColor="primary"
                sx={{
                  mb: 1.2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 1000,
                    color: "rgba(226,232,240,.65)",
                  },
                  "& .Mui-selected": { color: "#e5e7eb" },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: 3,
                    bgcolor: "rgba(56,189,248,.85)",
                  },
                }}
              >
                <Tab value="positions" label="Positions" />
                <Tab value="orders" label="Open orders" />
                <Tab value="history" label="History" />
              </Tabs>

              <Divider sx={{ mb: 1.4, borderColor: "rgba(148,163,184,.16)" }} />

              {mainTab === "positions" ? (
                <PositionsTable embedded loading={loading} positions={filteredPositions} />
              ) : mainTab === "orders" ? (
                <Box sx={{ color: GLASS.sub, fontSize: 13 }}>
                  Open orders — подключим отдельный компонент (Firestore/Functions).
                </Box>
              ) : (
                <LedgerTable embedded loading={loading} rows={filteredLedger} />
              )}
            </GlassCard>
          </Grid>
        </Grid>
      </Box>

      {/* ===== Modals ===== */}
      <DepositWithdrawModal
        open={walletOpen}
        mode={walletMode}
        onClose={() => setWalletOpen(false)}
        portfolioBalanceUsd={Number(totalUsd || 0)}
        walletAddress={address || ""}
        walletUsdHint={"Instant"}
        onPick={(id) => {
          if (id === "wallet_deposit") {
            if (!isConnected) {
              setWalletOpen(false);
              setTimeout(() => connectWalletOnly(), 0);
              return;
            }
            setWalletOpen(false);
            setWalletDepositOpen(true);
            return;
          }

          if (id === "transfer_crypto") {
            setWalletOpen(false);
            setTransferCryptoOpen(true);
            return;
          }
        }}
      />

      <WalletDepositSwapModal
        open={walletDepositOpen}
        onClose={() => setWalletDepositOpen(false)}
        platformHotWallet=""
        portfolioBalanceUsd={Number(totalUsd || 0)}
        prefetchedAvail={multiAvail}
        prefetchedLoading={multiLoading}
        prefetchedError={multiErr}
      />

      <TransferCryptoModal
        open={transferCryptoOpen}
        onClose={() => setTransferCryptoOpen(false)}
        defaultChainId={56}
        defaultToken="USDT"
      />
    </Box>
  );
}
